import { FpsWatchdog, type FpsStats } from "./fps-watchdog";
import type { PerfProfileStore } from "./perf-profile-store";

/**
 * Adaptive perf coordinator. Runs in the background, watches the FPS
 * watchdog while a transport is playing, and triggers a one-shot runtime
 * downgrade when sustained jank is detected.
 *
 * Conservative by design:
 * - Decisions are taken **only during playback** (idle FPS is noise).
 * - Needs **5 s of cumulative playback** before the first decision so we
 *   don't punish the warm-up period (samples decoding, textures uploading).
 * - **30 s lockout** between downgrades to let the previous step settle.
 * - **Max one downgrade per session** (we only have one realistic step:
 *   particles + sampler-strategy → off; further dial-down requires
 *   reverb-bypass which is deferred to a later iteration).
 * - **Disabled when the user forced a tier**: explicit beats automatic.
 *
 * The coordinator does **not** implement re-upgrade. Bouncing between
 * tiers mid-session is a worse experience than starting one tier lower.
 */

const TICK_INTERVAL_MS = 2_000;
const PLAYBACK_WARMUP_MS = 5_000;
const POST_DOWNGRADE_LOCKOUT_MS = 30_000;
const MAX_DOWNGRADES = 1;

/**
 * Espacement des relevés de FPS publiés à `onSample`, en temps de lecture
 * cumulé. Large exprès: le but est un signal de santé sur la durée, pas une
 * courbe. À 2 s de tick, un relevé toutes les 5 min donne une poignée de
 * points par session au lieu de plusieurs centaines.
 */
const SAMPLE_REPORT_INTERVAL_MS = 300_000;

export interface PlaybackProbe {
  /** Whether a transport is currently playing back (any kind: song, exercise, free). */
  isPlaying(): boolean;
}

export interface CoordinatorListeners {
  /** Optional hook fired the first time a downgrade is applied. The UI uses it to surface a toast. */
  onDowngrade?: () => void;
  /**
   * Optional periodic FPS reading, published during playback only and
   * heavily spaced (see `SAMPLE_REPORT_INTERVAL_MS`). Exists so product
   * analytics can observe real-device rendering health without the
   * coordinator having to know anything about the network.
   */
  onSample?: (stats: FpsStats) => void;
}

export class PerfCoordinator {
  private playbackElapsed = 0;
  private downgrades = 0;
  private lastDowngradeAt = 0;
  private lastTickAt = 0;
  private lastSampleReportAt = 0;

  private timerId: ReturnType<typeof setInterval> | null = null;

  constructor(
    private readonly store: PerfProfileStore,
    private readonly watchdog: FpsWatchdog,
    private readonly playback: PlaybackProbe,
    private readonly listeners: CoordinatorListeners = {},
  ) {}

  start(): void {
    if (this.timerId !== null || typeof window === "undefined") {
      return;
    }

    this.lastTickAt = performance.now();
    this.timerId = setInterval(() => this.tick(), TICK_INTERVAL_MS);
  }

  stop(): void {
    if (this.timerId !== null) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }

  private tick(): void {
    const now = performance.now();
    const elapsedSinceLastTick = now - this.lastTickAt;
    this.lastTickAt = now;

    // Accumulate playback time (and only that). Idle minutes don't move
    // the warm-up clock forward, so a user who opens the app, lets it sit,
    // then plays a piece gets a fair 5 s of grace.
    if (this.playback.isPlaying()) {
      this.playbackElapsed += elapsedSinceLastTick;
      this.maybeReportSample();
    }

    if (this.store.value.source === "user-forced") {
      return;
    }

    if (!this.playback.isPlaying()) {
      return;
    }

    if (this.playbackElapsed < PLAYBACK_WARMUP_MS) {
      return;
    }

    // Bail-out conditions, cheapest first.
    if (this.downgrades >= MAX_DOWNGRADES) {
      return;
    }

    if (now - this.lastDowngradeAt < POST_DOWNGRADE_LOCKOUT_MS) {
      return;
    }

    if (!this.watchdog.isJanky()) {
      return;
    }

    this.applyDowngrade(now);
  }

  /**
   * Publishes an FPS reading every `SAMPLE_REPORT_INTERVAL_MS` of *playback*.
   * Idle time never moves the clock, so a tab left open all afternoon reports
   * nothing — only actual rendering is worth measuring.
   */
  private maybeReportSample(): void {
    if (this.listeners.onSample === undefined) {
      return;
    }

    if (this.playbackElapsed - this.lastSampleReportAt < SAMPLE_REPORT_INTERVAL_MS) {
      return;
    }

    const stats = this.watchdog.getStats();

    if (stats === null) {
      return;
    }

    this.lastSampleReportAt = this.playbackElapsed;
    this.listeners.onSample(stats);
  }

  private applyDowngrade(now: number): void {
    this.store.applyAutoDowngrade(DOWNGRADE);

    this.downgrades += 1;
    this.lastDowngradeAt = now;
    this.listeners.onDowngrade?.();
  }
}

/** Le cran de dégradation, en un seul endroit. */
const DOWNGRADE = {
  effects: { particlesEnabled: false },
  // `outputLatencyHint` n'est volontairement pas de la partie : il ne se règle
  // qu'à la création de l'`AudioContext`, l'écrire ici donnerait l'illusion
  // d'un levier qui ne bouge rien avant un rechargement.
  audio: { samplerStrategy: "single" as const },
};
